import { lazy } from 'react';
import { createBrowserRouter, redirect } from "react-router-dom";
import { DashboardRoot } from "./components/dashboard/DashboardRoot";
import { microservicesRoutes, MicroservicesShell } from "./modules/microservices/mount";
import { IntegrationsShell, integrationsModuleRoutes } from "./modules/integrations/mount";

// ==========================================
// LAZY LOADED ROUTES - PERFORMANCE OPTIMIZED
// ==========================================
// All pages are lazy loaded to reduce initial bundle size
// Pages load on-demand when user navigates to them

// Overview Pages
const SuperDashboardPage = lazy(() => import("./components/dashboard/pages/overview/SuperDashboardPage").then(m => ({ default: m.SuperDashboardPage })));
const DashboardPage = lazy(() => import("./components/dashboard/pages/overview/DashboardPage").then(m => ({ default: m.DashboardPage })));
const DashboardBuilderPage = lazy(() => import("./components/dashboard/pages/overview/DashboardBuilderPage").then(m => ({ default: m.DashboardBuilderPage })));

// Onboarding Pages
const OnboardingOverviewPage = lazy(() => import("./components/dashboard/pages/onboarding/OnboardingOverviewPage").then(m => ({ default: m.OnboardingOverviewPage })));
const AddProjectPage = lazy(() => import("./components/dashboard/pages/onboarding/AddProjectPage").then(m => ({ default: m.AddProjectPage })));
const AddInfrastructurePage = lazy(() => import("./components/dashboard/pages/onboarding/AddInfrastructurePage").then(m => ({ default: m.AddInfrastructurePage })));
const AddMonitoringSourcesPage = lazy(() => import("./components/dashboard/pages/onboarding/AddMonitoringSourcesPage").then(m => ({ default: m.AddMonitoringSourcesPage })));
const ValidationStatusPage = lazy(() => import("./components/dashboard/pages/onboarding/ValidationStatusPage").then(m => ({ default: m.ValidationStatusPage })));
const SetupGuidePage = lazy(() => import("./components/dashboard/pages/onboarding/SetupGuidePage").then(m => ({ default: m.SetupGuidePage })));

// Monitoring Pages
const AppPerformancePage = lazy(() => import("./components/dashboard/pages/application/AppPerformancePage").then(m => ({ default: m.AppPerformancePage })));
const ServiceDetailPage = lazy(() => import("./components/dashboard/pages/application/ServiceDetailPage").then(m => ({ default: m.ServiceDetailPage })));
const ErrorsPage = lazy(() => import("./components/dashboard/pages/application/ErrorsPage").then(m => ({ default: m.ErrorsPage })));
const ErrorDetailPage = lazy(() => import("./components/dashboard/pages/application/ErrorDetailPage").then(m => ({ default: m.ErrorDetailPage })));
const LogsPage = lazy(() => import("./components/dashboard/pages/application/LogsPage").then(m => ({ default: m.LogsPage })));
const UserSessionsPage = lazy(() => import("./components/dashboard/pages/application/UserSessionsPage").then(m => ({ default: m.UserSessionsPage })));
const AnalyticsPage = lazy(() => import("./components/dashboard/pages/application/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const AlertsPage = lazy(() => import("./components/dashboard/pages/logs/AlertsPage").then(m => ({ default: m.AlertsPage })));

// Server Management Pages
const ServersPage = lazy(() => import("./components/dashboard/pages/infrastructure/ServersPage").then(m => ({ default: m.ServersPage })));
const NetworksPage = lazy(() => import("./components/dashboard/pages/infrastructure/NetworksPage").then(m => ({ default: m.NetworksPage })));
const DatabasesPage = lazy(() => import("./components/dashboard/pages/databases/DatabasesPage").then(m => ({ default: m.DatabasesPage })));
const ContainersPage = lazy(() => import("./components/dashboard/pages/infrastructure/ContainersPage").then(m => ({ default: m.ContainersPage })));
const ServicesPage = lazy(() => import("./components/dashboard/pages/infrastructure/ServicesPage").then(m => ({ default: m.ServicesPage })));
const PatchManagementPage = lazy(() => import("./components/dashboard/pages/infrastructure/PatchManagementPage").then(m => ({ default: m.PatchManagementPage })));
const FineTuningPage = lazy(() => import("./components/dashboard/pages/infrastructure/FineTuningPage").then(m => ({ default: m.FineTuningPage })));
const CronScriptsPage = lazy(() => import("./components/dashboard/pages/infrastructure/CronScriptsPage").then(m => ({ default: m.CronScriptsPage })));

// AI Pages
const AutoHealingPage = lazy(() => import("./components/dashboard/pages/ai/AutoHealingPage").then(m => ({ default: m.AutoHealingPage })));
const PredictiveAnalyticsPage = lazy(() => import("./components/dashboard/pages/ai/PredictiveAnalyticsPage").then(m => ({ default: m.PredictiveAnalyticsPage })));
const SmartAlertsPage = lazy(() => import("./components/dashboard/pages/ai/SmartAlertsPage").then(m => ({ default: m.SmartAlertsPage })));
const AIInsightsPage = lazy(() => import("./components/dashboard/pages/ai/AIInsightsPage").then(m => ({ default: m.AIInsightsPage })));
const AIAssistantPage = lazy(() => import("./components/dashboard/pages/ai/AIAssistantPage").then(m => ({ default: m.AIAssistantPage })));
const SelfHealingPage = lazy(() => import("./components/dashboard/pages/ai/SelfHealingPage").then(m => ({ default: m.SelfHealingPage })));
const SpikeAnalysisPage = lazy(() => import("./components/dashboard/pages/ai/SpikeAnalysisPage").then(m => ({ default: m.SpikeAnalysisPage })));

// Microservices Pages
const APIsPage = lazy(() => import("./components/dashboard/pages/infrastructure/APIsPage").then(m => ({ default: m.APIsPage })));
const WebsitesPage = lazy(() => import("./components/dashboard/pages/infrastructure/WebsitesPage").then(m => ({ default: m.WebsitesPage })));
const MicroservicesOverviewPage = lazy(() => import("./components/dashboard/pages/microservices/MicroservicesOverviewPage").then(m => ({ default: m.MicroservicesOverviewPage })));
const AddMicroservicePage = lazy(() => import("./components/dashboard/pages/microservices/AddMicroservicePage").then(m => ({ default: m.AddMicroservicePage })));
const ServiceMeshPage = lazy(() => import("./components/dashboard/pages/microservices/ServiceMeshPage").then(m => ({ default: m.ServiceMeshPage })));
const DistributedTracingPage = lazy(() => import("./components/dashboard/pages/microservices/DistributedTracingPage").then(m => ({ default: m.DistributedTracingPage })));
const AgentScriptsPage = lazy(() => import("./components/dashboard/pages/microservices/AgentScriptsPage").then(m => ({ default: m.AgentScriptsPage })));

// Ticketing Pages
const TicketDashboardPage = lazy(() => import("./components/dashboard/pages/ticketing/TicketDashboardPage").then(m => ({ default: m.TicketDashboardPage })));
const CreateTicketPage = lazy(() => import("./components/dashboard/pages/ticketing/CreateTicketPage").then(m => ({ default: m.CreateTicketPage })));
const MyTicketsPage = lazy(() => import("./components/dashboard/pages/ticketing/MyTicketsPage").then(m => ({ default: m.MyTicketsPage })));
const TeamTicketsPage = lazy(() => import("./components/dashboard/pages/ticketing/TeamTicketsPage").then(m => ({ default: m.TeamTicketsPage })));
const AIGeneratedTicketsPage = lazy(() => import("./components/dashboard/pages/ticketing/AIGeneratedTicketsPage").then(m => ({ default: m.AIGeneratedTicketsPage })));
const AutomationRulesPage = lazy(() => import("./components/dashboard/pages/ticketing/AutomationRulesPage").then(m => ({ default: m.AutomationRulesPage })));
const TicketIntegrationsPage = lazy(() => import("./components/dashboard/pages/ticketing/TicketIntegrationsPage").then(m => ({ default: m.TicketIntegrationsPage })));
const SLAManagementPage = lazy(() => import("./components/dashboard/pages/ticketing/SLAManagementPage").then(m => ({ default: m.SLAManagementPage })));
const TicketAnalyticsPage = lazy(() => import("./components/dashboard/pages/ticketing/TicketAnalyticsPage").then(m => ({ default: m.TicketAnalyticsPage })));

// Incident Commander Pages
const IncidentCommanderDashboard = lazy(() => import("./components/dashboard/pages/incident-commander/IncidentCommanderDashboard").then(m => ({ default: m.IncidentCommanderDashboard })));
const WarRoomPage = lazy(() => import("./components/dashboard/pages/incident-commander/WarRoomPage").then(m => ({ default: m.WarRoomPage })));
const IncidentPredictionsPage = lazy(() => import("./components/dashboard/pages/incident-commander/IncidentPredictionsPage").then(m => ({ default: m.IncidentPredictionsPage })));
const KnowledgeGraphPage = lazy(() => import("./components/dashboard/pages/incident-commander/KnowledgeGraphPage").then(m => ({ default: m.KnowledgeGraphPage })));
const EngineerAssignmentPage = lazy(() => import("./components/dashboard/pages/incident-commander/EngineerAssignmentPage").then(m => ({ default: m.EngineerAssignmentPage })));

// Incident Management Pages
const IncidentsPage = lazy(() => import("./components/dashboard/pages/incidents/IncidentsPage").then(m => ({ default: m.IncidentsPage })));

// Team Pages
const TeamMembersPage = lazy(() => import("./components/dashboard/pages/team/TeamMembersPage").then(m => ({ default: m.TeamMembersPage })));
const RolesPermissionsPage = lazy(() => import("./components/dashboard/pages/team/RolesPermissionsPage").then(m => ({ default: m.RolesPermissionsPage })));
const CollaborationToolsPage = lazy(() => import("./components/dashboard/pages/team/CollaborationToolsPage").then(m => ({ default: m.CollaborationToolsPage })));
const TeamChatPage = lazy(() => import("./modules/team-chat/pages/TeamChatPage").then(m => ({ default: m.TeamChatPage })));

// Achievements Pages
const BadgesPage = lazy(() => import("./components/dashboard/pages/achievements/BadgesPage").then(m => ({ default: m.BadgesPage })));

// System Pages
const SettingsPage = lazy(() => import("./components/dashboard/pages/settings/SettingsPage").then(m => ({ default: m.SettingsPage })));
const AgentManagementPage = lazy(() =>
  import("./components/dashboard/pages/settings/AgentManagementPage").then((m) => ({ default: m.AgentManagementPage })),
);
const IntegrationsPage = lazy(() => import("./components/dashboard/pages/integrations/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const CloudProvidersPage = lazy(() => import("./components/dashboard/pages/integrations/CloudProvidersPage").then(m => ({ default: m.CloudProvidersPage })));
const SecurityEventsPage = lazy(() => import("./components/dashboard/pages/security/SecurityEventsPage").then(m => ({ default: m.SecurityEventsPage })));

// Payment Pages
const BillingPage = lazy(() => import("./components/dashboard/pages/payment/BillingPage").then(m => ({ default: m.BillingPage })));
const SubscriptionPage = lazy(() => import("./components/dashboard/pages/payment/SubscriptionPage").then(m => ({ default: m.SubscriptionPage })));
const InvoicesPage = lazy(() => import("./components/dashboard/pages/payment/InvoicesPage").then(m => ({ default: m.InvoicesPage })));

// Support Pages
const SupportPage = lazy(() => import("./components/dashboard/pages/support/SupportPage").then(m => ({ default: m.SupportPage })));
const HelpCenterPage = lazy(() => import("./components/dashboard/pages/support/HelpCenterPage").then(m => ({ default: m.HelpCenterPage })));
const ContactSupportPage = lazy(() => import("./components/dashboard/pages/support/ContactSupportPage").then(m => ({ default: m.ContactSupportPage })));
const DocumentationPage = lazy(() => import("./components/dashboard/pages/support/DocumentationPage").then(m => ({ default: m.DocumentationPage })));

// Time Machine Pages
const TimelineExplorerPage = lazy(() => import("./components/dashboard/pages/time-machine/TimelineExplorerPage").then(m => ({ default: m.TimelineExplorerPage })));
const InfrastructureReplayPage = lazy(() => import("./components/dashboard/pages/time-machine/InfrastructureReplayPage").then(m => ({ default: m.InfrastructureReplayPage })));
const IncidentPlaybackPage = lazy(() => import("./components/dashboard/pages/time-machine/IncidentPlaybackPage").then(m => ({ default: m.IncidentPlaybackPage })));
const SystemSnapshotsPage = lazy(() => import("./components/dashboard/pages/time-machine/SystemSnapshotsPage").then(m => ({ default: m.SystemSnapshotsPage })));
const StateDiffAnalyzerPage = lazy(() => import("./components/dashboard/pages/time-machine/StateDiffAnalyzerPage").then(m => ({ default: m.StateDiffAnalyzerPage })));

// Not Found Page
const NotFoundPage = lazy(() => import("./components/dashboard/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));

// Legal (in-app, same shell as dashboard)
const DashboardTermsPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardTermsPage })));
const DashboardPrivacyPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardPrivacyPage })));
const DashboardCookiePage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardCookiePage })));
const DashboardDpaPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardDpaPage })));
const DashboardEnterpriseSaaSPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardEnterpriseSaaSPage })));
const DashboardSlaPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardSlaPage })));
const DashboardBillingTermsPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardBillingTermsPage })));
const DashboardSecurityAddendumPage = lazy(() => import("./components/pages/legal/DashboardLegalPages").then(m => ({ default: m.DashboardSecurityAddendumPage })));

export const router = createBrowserRouter([
  {
    path: "/",
    Component: DashboardRoot,
    children: [
      // Overview Section
      { index: true, Component: SuperDashboardPage },
      { path: "overview/super-dashboard", Component: SuperDashboardPage },
      { path: "overview/dashboard", Component: DashboardPage },
      { path: "overview/dashboard-builder", Component: DashboardBuilderPage },
      
      // Onboarding Section
      { path: "onboarding/overview", Component: OnboardingOverviewPage },
      { path: "onboarding/add-project", Component: AddProjectPage },
      { path: "onboarding/add-infrastructure", Component: AddInfrastructurePage },
      { path: "onboarding/add-monitoring-sources", Component: AddMonitoringSourcesPage },
      { path: "onboarding/validation-status", Component: ValidationStatusPage },
      { path: "onboarding/setup-guide", Component: SetupGuidePage },
      
      // Monitoring Section
      { path: "monitoring/app-performance", Component: AppPerformancePage },
      { path: "monitoring/app-performance/:serviceId", Component: ServiceDetailPage },
      { path: "monitoring/errors", Component: ErrorsPage },
      { path: "monitoring/errors/:errorId", Component: ErrorDetailPage },
      { path: "monitoring/logs", Component: LogsPage },
      { path: "monitoring/user-sessions", Component: UserSessionsPage },
      { path: "monitoring/analytics", Component: AnalyticsPage },
      { path: "monitoring/alerts", Component: AlertsPage },
      
      // Server Management Section
      { path: "server-mgmt/servers", Component: ServersPage },
      { path: "server-mgmt/networks", Component: NetworksPage },
      { path: "server-mgmt/databases", Component: DatabasesPage },
      { path: "server-mgmt/containers", Component: ContainersPage },
      { path: "server-mgmt/services", Component: ServicesPage },
      { path: "server-mgmt/patch-management", Component: PatchManagementPage },
      { path: "server-mgmt/fine-tuning", Component: FineTuningPage },
      { path: "server-mgmt/cron-scripts", Component: CronScriptsPage },
      
      // AI Section
      { path: "ai/auto-healing", Component: AutoHealingPage },
      { path: "ai/predictive-analytics", Component: PredictiveAnalyticsPage },
      { path: "ai/smart-alerts", Component: SmartAlertsPage },
      { path: "ai/insights", Component: AIInsightsPage },
      { path: "ai/assistant", Component: AIAssistantPage },
      { path: "ai/self-healing", Component: SelfHealingPage },
      { path: "ai/spike-analysis", Component: SpikeAnalysisPage },
      
      // Microservices Section (legacy routes preserved + new observability module)
      {
        path: "microservices",
        Component: MicroservicesShell,
        children: [
          { path: "overview", Component: MicroservicesOverviewPage },
          { path: "add", Component: AddMicroservicePage },
          { path: "service-mesh", Component: ServiceMeshPage },
          { path: "distributed-tracing", Component: DistributedTracingPage },
          { path: "agent-scripts", Component: AgentScriptsPage },
          { path: "apis", Component: APIsPage },
          { path: "websites", Component: WebsitesPage },
          ...microservicesRoutes,
        ],
      },
      
      // Ticketing Section
      { path: "ticketing/dashboard", Component: TicketDashboardPage },
      { path: "ticketing/create", Component: CreateTicketPage },
      { path: "ticketing/my-tickets", Component: MyTicketsPage },
      { path: "ticketing/team-tickets", Component: TeamTicketsPage },
      { path: "ticketing/ai-generated", Component: AIGeneratedTicketsPage },
      { path: "ticketing/automation", Component: AutomationRulesPage },
      { path: "ticketing/integrations", Component: TicketIntegrationsPage },
      { path: "ticketing/sla", Component: SLAManagementPage },
      { path: "ticketing/analytics", Component: TicketAnalyticsPage },
      
      // Incident Commander Section
      { path: "incident-commander/dashboard", Component: IncidentCommanderDashboard },
      { path: "incident-commander/war-room/:warRoomId", Component: WarRoomPage },
      { path: "incident-commander/predictions", Component: IncidentPredictionsPage },
      { path: "incident-commander/knowledge-graph", Component: KnowledgeGraphPage },
      { path: "incident-commander/engineers", Component: EngineerAssignmentPage },
      
      // Incident Management Section
      { path: "incidents", Component: IncidentsPage },
      
      // Team Section
      { path: "team/members", Component: TeamMembersPage },
      { path: "team/chat", Component: TeamChatPage },
      { path: "roles-permissions", Component: RolesPermissionsPage },
      { path: "collaboration", Component: CollaborationToolsPage },
      
      // Achievements Section
      { path: "achievements/badges", Component: BadgesPage },
      
      // System Section
      { path: "system/agents", Component: AgentManagementPage },
      { path: "system/settings", Component: SettingsPage },
      {
        path: "system/integrations",
        Component: IntegrationsShell,
        children: [{ index: true, Component: IntegrationsPage }],
      },
      { path: "system/cloud-providers", Component: CloudProvidersPage },
      { path: "system/security-events", Component: SecurityEventsPage },

      // Integrations module — Notification Center + provider sub-routes
      ...integrationsModuleRoutes,
      
      // Payment Section
      { path: "payment/billing", Component: BillingPage },
      { path: "payment/subscription", Component: SubscriptionPage },
      { path: "payment/invoices", Component: InvoicesPage },
      { path: "billing", Component: BillingPage },
      { path: "subscription", Component: SubscriptionPage },
      { path: "invoices", Component: InvoicesPage },
      
      // Support Section
      { path: "support", Component: SupportPage },
      { path: "support/help-center", Component: HelpCenterPage },
      { path: "support/contact", Component: ContactSupportPage },
      { path: "support/documentation", Component: DocumentationPage },
      { path: "help-center", Component: HelpCenterPage },
      { path: "contact-support", Component: ContactSupportPage },
      { path: "documentation", Component: DocumentationPage },

      // Legal & trust (readable inside dashboard)
      { path: "legal/terms-of-service", Component: DashboardTermsPage },
      { path: "legal/privacy-policy", Component: DashboardPrivacyPage },
      { path: "legal/cookie-policy", Component: DashboardCookiePage },
      { path: "legal/dpa", Component: DashboardDpaPage },
      { path: "legal/enterprise-saas", Component: DashboardEnterpriseSaaSPage },
      { path: "legal/sla", Component: DashboardSlaPage },
      { path: "legal/billing", Component: DashboardBillingTermsPage },
      { path: "legal/security-addendum", Component: DashboardSecurityAddendumPage },
      
      // Time Machine Section
      { path: "time-machine/timeline-explorer", Component: TimelineExplorerPage },
      { path: "time-machine/infrastructure-replay", Component: InfrastructureReplayPage },
      { path: "time-machine/incident-playback", Component: IncidentPlaybackPage },
      { path: "time-machine/system-snapshots", Component: SystemSnapshotsPage },
      { path: "time-machine/state-diff-analyzer", Component: StateDiffAnalyzerPage },

      // After login the browser can still be on /signin or /signup (public shell used pushState).
      // Remap so the data router, index route, and sidebar stay aligned.
      { path: "signin", loader: () => redirect("/") },
      { path: "signup", loader: () => redirect("/") },
      
      // 404 Not Found
      { path: "*", Component: NotFoundPage },
    ],
  },
]);